import { find, isEmpty, toNumber } from 'lodash';
import { useRouter } from 'next/router';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import useScreenFocus from '@/hooks/useScreenFocus';
import useToast from '@/hooks/useToast';
import { getAuthToken } from '@/recoil/atoms/common/auth';
import { SearchHospitalService } from '@/services/hospital/SearchHospitalService';
import { ROUTES } from '@/types/common';
import {
  HospitalSearchQueries,
  RegionByDepthVo,
  RegionName,
  SearchKoreaAddressV2Vo,
  SelectedRegion,
  UserCoordinate,
} from '@/types/hospital';
import { faceTofaceLogger, getPageName } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { updateSearchParams } from '@/utils/url';

import useGetAddressToCoordinateByIp from './useGetAddressToCoordinateByIp';
import useGetUserAddressByLocation from './useGetUserAddressByLocation';
import useGetUserAddresses from './useGetUserAddresses';
import useSearchKoreaAddressV2 from './useSearchKoreaAddressV2';

const initSidoSeoul = {
  centroidLatitude: 37.55191888765841,
  centroidLongitude: 126.99182510819786,
  displayName: '서울',
  regionCode: '11',
  regionName: '서울특별시',
};

const useUserLocation = () => {
  const router = useRouter();
  const pageName = getPageName(router);
  const isFocused = useScreenFocus();
  const isLoggedIn = !!getAuthToken();
  const { showToast } = useToast();

  const { latitude: queryLatitude, longitude: queryLongitude } = router.query as HospitalSearchQueries;

  const queryCoord = useMemo(() => {
    if (!queryLatitude || !queryLongitude) {
      return SearchHospitalService.initializeUserCoordinate();
    }

    const latitude = toNumber(queryLatitude);
    const longitude = toNumber(queryLongitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      return SearchHospitalService.initializeUserCoordinate();
    }

    return { latitude, longitude };
  }, [queryLatitude, queryLongitude]);

  const [isRegionFilterOpened, setIsRegionFilterOpened] = useState<boolean>(false);
  const [regionByDepth, setRegionByDepth] = useState<RegionByDepthVo>();
  const [selectedRegion, setSelectedRegion] = useState<SelectedRegion>({
    siDo: initSidoSeoul,
    siGunGu: null,
    dong: null,
  });

  const isSejong = selectedRegion.siDo?.regionCode === '36';
  const isLocationSelected = () => {
    return !!regionByDepth || (!!queryLatitude && !!queryLongitude);
  };

  const { data: siDoList = [] } = useSearchKoreaAddressV2({
    siDoCode: null,
    siGunGuCode: null,
  });
  const { data: siGunGuList = [] } = useSearchKoreaAddressV2({
    siDoCode: isSejong ? '' : selectedRegion.siDo?.regionCode || '',
    siGunGuCode: null,
  });
  const { data: dongList = [] } = useSearchKoreaAddressV2({
    siDoCode: selectedRegion.siDo?.regionCode || '',
    siGunGuCode: isSejong ? null : selectedRegion.siGunGu?.regionCode || '',
  });

  const { data: userCoordinateByIp } = useGetAddressToCoordinateByIp();

  const { data: sigunguDongli = SearchHospitalService.initializeUserSigunguDongli(), refetch: addressFetch } =
    useGetUserAddressByLocation(queryCoord.latitude, queryCoord.longitude);

  const { data: addresses, refetch: refetchAddresses } = useGetUserAddresses();

  const getHeaderAddress = () => {
    let address = `${sigunguDongli.dongLi}`;

    if (regionByDepth?.siDoName) {
      if (!regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName}`;
      } else if (regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siGunGuName}`;
      } else if (regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.legalEupMyeonDongName}`;
      } else if (!regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.legalEupMyeonDongName}`;
      }
    }
    return address;
  };

  const getHeaderFullAddress = () => {
    let address = `${sigunguDongli.sigungu} ${sigunguDongli.dongLi}`;

    if (regionByDepth?.siDoName) {
      if (!regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName}`;
      } else if (regionByDepth.siGunGuName && !regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName} ${regionByDepth.siGunGuName}`;
      } else if (regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siGunGuName} ${regionByDepth.legalEupMyeonDongName}`;
      } else if (!regionByDepth.siGunGuName && regionByDepth.legalEupMyeonDongName) {
        address = `${regionByDepth.siDoName} ${regionByDepth.legalEupMyeonDongName}`;
      }
    }

    return address;
  };

  const switchRegionFilter = () => {
    // 지역필터 모달 오픈시
    if (!isRegionFilterOpened) {
      faceTofaceLogger({
        pageName,
        eventName: 'click_region_filter',
      });
    }

    setIsRegionFilterOpened(!isRegionFilterOpened);
  };

  const selectRegion = (region: RegionName) => {
    setRegionByDepth({
      siDoName: region.siDoName,
      siGunGuName: region?.siGunGuName,
      legalEupMyeonDongName: region?.dongName,
    });
    setIsRegionFilterOpened(false);
  };

  const changeSiDo = useCallback((siDo: SearchKoreaAddressV2Vo, isAll?: boolean) => {
    setSelectedRegion({
      siDo,
      siGunGu: null,
      dong: null,
    });

    if (isAll) {
      selectRegion({
        siDoName: siDo.regionName,
        siGunGuName: undefined,
        dongName: undefined,
      });
    }
  }, []);

  const clickSiDo = (item: SearchKoreaAddressV2Vo, isAll?: boolean) => (event: MouseEvent<HTMLDivElement>) => {
    faceTofaceLogger({
      pageName,
      eventName: 'click_region_filter_find',
      payload: {
        sector_1: item.regionName,
      },
    });
    event.stopPropagation();
    changeSiDo(item, isAll);
  };

  const changeSiGunGu = (siGunGu: SearchKoreaAddressV2Vo, isAll?: boolean) => {
    if (!selectedRegion?.siDo) return;

    if (!isAll && siGunGu.regionCode === selectedRegion.siGunGu?.regionCode) {
      setSelectedRegion({
        ...selectedRegion,
        siGunGu: null,
        dong: null,
      });
      return;
    }

    setSelectedRegion({
      ...selectedRegion,
      siGunGu,
      dong: null,
    });

    if (isAll) {
      selectRegion({
        siDoName: selectedRegion.siDo.regionName,
        siGunGuName: siGunGu.regionName,
      });
    }
  };

  const clickSiGunGu = (item: SearchKoreaAddressV2Vo, isAll?: boolean) => (event: MouseEvent<HTMLDivElement>) => {
    faceTofaceLogger({
      pageName,
      eventName: 'click_region_filter_find',
      payload: {
        sector_1: selectedRegion.siDo?.regionName ?? '',
        sector_2: item.regionName,
      },
    });
    event.stopPropagation();
    changeSiGunGu(item, isAll);
  };

  const changeDong = (dong: SearchKoreaAddressV2Vo) => {
    if (!dong.regionCode || !selectedRegion.siDo) return;

    setSelectedRegion({
      ...selectedRegion,
      dong,
    });

    selectRegion({
      siDoName: selectedRegion.siDo.regionName,
      siGunGuName: selectedRegion.siGunGu?.regionName,
      dongName: dong?.regionName,
    });
  };

  const clickDong = (item: SearchKoreaAddressV2Vo) => (event: MouseEvent<HTMLDivElement>) => {
    faceTofaceLogger({
      pageName,
      eventName: 'click_region_filter_find',
      payload: {
        sector_1: selectedRegion.siDo?.regionName ?? '',
        sector_2: selectedRegion.siGunGu?.regionName ?? '',
        sector_3: item.regionName,
      },
    });
    event.stopPropagation();
    changeDong(item);
  };

  const initRegionFilter = () => {
    setSelectedRegion({
      siDo: null,
      siGunGu: null,
      dong: null,
    });
    setRegionByDepth(undefined);
  };

  const updatePureCoordinate = (coord: UserCoordinate) => {
    updateSearchParams([
      {
        key: 'latitude',
        value: coord.latitude,
      },
      {
        key: 'longitude',
        value: coord.longitude,
      },
    ]);
  };

  const updateCoordinate = useCallback((coord: UserCoordinate) => {
    initRegionFilter();
    updatePureCoordinate(coord);
  }, []);

  const getCurrentLocation = (nonLog?: boolean) => {
    if (!nonLog) {
      faceTofaceLogger({
        pageName,
        eventName: 'click_region_filter_my_location',
      });
    }
    requestGeolocationPermission();
    setIsRegionFilterOpened(false);
  };

  const requestPureGeolocationPermission = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        updatePureCoordinate({
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
      },
      () => {
        return;
      },
      { enableHighAccuracy: true }
    );
  }, []);

  const requestGeolocationPermission = () => {
    initRegionFilter();
    if (!navigator.geolocation) {
      showToast({
        message: '다른 브라우저로 해당 기능을 이용해주세요',
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        updatePureCoordinate({
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
      },
      () => {
        showToast({
          message: '위치 사용을 허용을 해야 내 위치 설정이 가능해요',
        });
      },
      { enableHighAccuracy: true }
    );
  };

  const selectSavedAddress = (coordiname: UserCoordinate) => () => {
    updateCoordinate(coordiname);
    switchRegionFilter();
  };

  const goToAppMyPageAddressScreen = () => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: 'AddressStack',
        params: { screen: ROUTES.ADDRESS.HOME.appScreenName },
      });
    }
  };

  const getRegionFilterProps = () => {
    return {
      isRegionFilterOpened,
      siDoList,
      siGunGuList,
      sigunguDongli,
      dongList,
      selectedRegion,
      isSejong,
      addresses,
      coordinate: queryCoord,
      getCurrentLocation,
      switchRegionFilter,
      selectSavedAddress,
      goToAppMyPageAddressScreen,
      clickSiDo,
      clickSiGunGu,
      clickDong,
    };
  };

  const getCoordinateToString = () => {
    return {
      latitude: String(queryCoord.latitude),
      longitude: String(queryCoord.longitude),
    };
  };

  useEffect(() => {
    if (!isEmpty(siDoList) && !selectedRegion.siDo) {
      const seoul = find(siDoList, { regionCode: '11' });

      if (seoul) {
        setSelectedRegion({ siDo: seoul, dong: null, siGunGu: null });
      }
    }
  }, [siDoList, selectedRegion.siDo, changeSiDo]);

  useEffect(() => {
    addressFetch();
  }, [queryCoord, addressFetch]);

  useEffect(() => {
    if (queryLatitude && queryLongitude) return;
    if (!userCoordinateByIp) return requestPureGeolocationPermission();

    updateCoordinate(userCoordinateByIp);
  }, [queryLatitude, queryLongitude, requestPureGeolocationPermission, updateCoordinate, userCoordinateByIp]);

  useEffect(() => {
    if (isFocused && isLoggedIn) {
      refetchAddresses();
    }
  }, [isFocused, isLoggedIn]);

  return {
    coordinate: queryCoord,
    isLocationSelected,
    isRegionFilterOpened,
    regionByDepth,
    switchRegionFilter,
    getHeaderAddress,
    getCoordinateToString,
    getRegionFilterProps,
    getHeaderFullAddress,
  };
};

export default useUserLocation;
